'use client'

import { useGraphic } from '@/lib/features/graphic/GraphicContext'
import { useCallback, useEffect, useRef } from 'react'
import { Box, useGraphicPreview } from '../../../lib/features/graphic/GraphicPreviewContext'
import {
  getClientRects,
  getEditingBoundingBox,
  getSelectionNodeInRect,
  getTextContent,
  isEditableTextNode,
  removeNodes,
  useEvent,
} from '../utils'
import GraphicPreviewIframe from './GraphicPreviewIframe'
import GraphicPreviewOverlay from './GraphicPreviewOverlay'

export default function GraphicPreview() {
  const { graphic, isLoading, updateGraphicHtml, undo, redo } = useGraphic()
  const {
    selectedNodes,
    setSelectedNodes,
    scale,
    setHighlightBoxes,
    editingNode,
    setEditingNode,
    editingText,
    setEditingText,
    setEditingBox,
    clearAllStates,
    exitEditMode,
  } = useGraphicPreview()

  const iframeRef = useRef<HTMLIFrameElement>(null)

  const handleIframeReady = useCallback((iframe: HTMLIFrameElement | null) => {
    iframeRef.current = iframe
  }, [])

  const getIframeHtml = (): string | null => {
    const iframeDoc = getIframeDoc()
    if (!iframeDoc) return null

    return iframeDoc.documentElement.outerHTML
  }

  const updateGraphicWithCurrentHtml = async () => {
    const currentHtml = getIframeHtml()

    if (!currentHtml) {
      console.error('No HTML content available to update')
      return
    }

    updateGraphicHtml(currentHtml)
  }

  const updateHighlightBoxes = useCallback(() => {
    const iframeDoc = getIframeDoc()
    if (!selectedNodes.length || !iframeDoc) {
      setHighlightBoxes([])
      return
    }

    const boxes: Box[] = []

    selectedNodes.forEach((element) => {
      const rects = getClientRects(element, iframeDoc)

      Array.from(rects).forEach((rect) => {
        const top = rect.top
        const left = rect.left
        const width = rect.width
        const height = rect.height

        boxes.push({ top, left, width, height })
      })
    })

    setHighlightBoxes(boxes)
  }, [selectedNodes, setHighlightBoxes])

  // Detect if we're on Mac (for keyboard shortcuts)
  const isMac = useCallback(() => {
    return typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0
  }, [])

  // Handle undo/redo keyboard shortcuts
  const handleUndoRedoKeyDown = useEvent((event: KeyboardEvent) => {
    // Don't interfere with text editing
    if (editingNode) {
      return
    }

    const isMetaOrCtrl = isMac() ? event.metaKey : event.ctrlKey

    if (isMetaOrCtrl && event.key.toLowerCase() === 'z') {
      event.preventDefault()

      if (event.shiftKey) {
        // Shift+Cmd+Z (Mac) or Shift+Ctrl+Z (Windows/Linux) = Redo
        redo()
      } else {
        // Cmd+Z (Mac) or Ctrl+Z (Windows/Linux) = Undo
        undo()
      }
    } else if (isMetaOrCtrl && event.key.toLowerCase() === 'y' && !isMac()) {
      // Ctrl+Y (Windows/Linux only) = Redo
      event.preventDefault()
      redo()
    }
  })

  // This is the keydown handler for the iframe, it is used to handle the delete and backspace keys
  const handleKeyDown = useEvent(async (event: KeyboardEvent, _iframeDoc: Document) => {
    // Handle undo/redo shortcuts first
    handleUndoRedoKeyDown(event)

    if (editingNode) {
      return
    }

    // Make sure the iframe is focused
    const iframe = iframeRef.current
    if (event.currentTarget === document && document.activeElement !== iframe) {
      return
    }

    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault()

      if (selectedNodes && selectedNodes.length) {
        removeNodes(selectedNodes)
        setSelectedNodes([])
        setHighlightBoxes([])

        await updateGraphicWithCurrentHtml()
      }
    }
  })

  const handleMouseDown = useEvent((event: MouseEvent, iframeDoc: Document) => {
    const x = event.clientX
    const y = event.clientY

    const clickSelectionBox = { top: y - 2, left: x - 2, width: 4, height: 4 }

    const selectedNode = getSelectionNodeInRect(clickSelectionBox, iframeDoc)

    if (selectedNode) {
      if (
        selectedNode.nodeName.toLowerCase() === 'html' ||
        selectedNode.nodeName.toLowerCase() === 'body'
      ) {
        setSelectedNodes([])
      } else if (selectedNodes.includes(selectedNode)) {
        setSelectedNodes([])
      } else {
        setSelectedNodes([selectedNode])
      }
    }

    exitEditMode()

    // Prevent default to avoid text selection
    event.preventDefault()
  })

  const getIframeDoc = (): Document | null => {
    if (!iframeRef.current) return null
    const iframe = iframeRef.current
    return iframe.contentDocument || iframe.contentWindow?.document || null
  }

  // Handle double-click for text editing
  const handleDoubleClick = useEvent((event: MouseEvent, iframeDoc: Document) => {
    if (!iframeDoc) return

    const x = event.clientX
    const y = event.clientY

    const clickSelectionBox = { top: y - 2, left: x - 2, width: 4, height: 4 }
    const nodeAtPoint = getSelectionNodeInRect(clickSelectionBox, iframeDoc)

    if (!nodeAtPoint) return

    if (!isEditableTextNode(nodeAtPoint)) {
      return
    }

    const textContent = getTextContent(nodeAtPoint)
    const box = getEditingBoundingBox(nodeAtPoint, iframeDoc)

    if (box) {
      setEditingNode(nodeAtPoint)
      setEditingText(textContent)
      setEditingBox(box)

      // Clear selection when entering edit mode
      setSelectedNodes([])
      setHighlightBoxes([])

      event.preventDefault()
      event.stopPropagation()
    }
  })

  // Save edited text
  const saveEditedText = useEvent(async () => {
    if (!editingNode) return

    try {
      // Verify the node is still in the DOM before updating
      if (!editingNode.parentNode) {
        console.warn('Editing node is no longer in the DOM')
        exitEditMode()
        return
      }

      if (!isEditableTextNode(editingNode)) {
        console.log("not a text node, can't edit")
        return
      }

      // Get the original text content to compare
      const originalText = getTextContent(editingNode)

      // If text hasn't changed, just exit edit mode without updating HTML
      if (originalText === editingText) {
        console.log('Text unchanged, exiting edit mode without update')
        exitEditMode()
        return
      }

      console.log('editingNode', editingNode)
      console.log('Saving edited text:', editingText)

      editingNode.textContent = editingText

      console.log('Text updated successfully in iframe')

      exitEditMode()

      await updateGraphicWithCurrentHtml()
    } catch (error) {
      console.error('Error saving edited text:', error)
    }
  })

  // Handle key events for text editing (from the preview overlay)
  const handleEditKeyDown = useEvent((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      saveEditedText()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setSelectedNodes([])
      exitEditMode()
    }
  })

  const handleScroll = useEvent((iframeDoc: Document) => {
    updateHighlightBoxes()

    if (editingNode) {
      const box = getEditingBoundingBox(editingNode, iframeDoc)
      if (box) {
        setEditingBox(box)
      }
    }
  })

  // Focus the iframe to ensure it receives keyboard events
  useEffect(() => {
    if (selectedNodes.length > 0 && iframeRef.current && !editingNode) {
      iframeRef.current.focus()
    }
  }, [selectedNodes, editingNode])

  useEffect(() => {
    updateHighlightBoxes()
  }, [selectedNodes, scale])

  // Add global keyboard event listener for undo/redo when component is mounted
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      // Only handle undo/redo if the component has focus or iframe has focus
      const iframe = iframeRef.current
      const isComponentFocused =
        document.activeElement === iframe ||
        document.activeElement?.closest('.graphic-preview-container')

      if (isComponentFocused) {
        handleUndoRedoKeyDown(event)
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown)

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown)
    }
  }, [handleUndoRedoKeyDown])

  return (
    <div className="h-full overflow-hidden bg-gray-100">
      <div className="h-full">
        {graphic?.html ? (
          <div
            className="flex h-full w-full justify-center overflow-auto"
            style={{
              width: `${100.0 / scale}%`,
              height: `${100.0 / scale}%`,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
          >
            <div
              className="relative"
              style={{
                width: 'fit-content',
                height: 'fit-content',
              }}
            >
              <GraphicPreviewIframe
                onIframeReady={handleIframeReady}
                handleScroll={handleScroll}
                handleMouseDown={handleMouseDown}
                handleDoubleClick={handleDoubleClick}
                handleKeyDown={handleKeyDown}
                onHtmlRefreshed={clearAllStates}
              />

              <GraphicPreviewOverlay
                onEditingTextChange={setEditingText}
                onSaveEditedText={saveEditedText}
                onEditKeyDown={handleEditKeyDown}
              />
            </div>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-gray-500">
            No graphic to preview
          </div>
        )}
      </div>

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="flex items-center space-x-3 rounded-lg bg-white p-4 shadow-lg">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-500"></div>
            <span className="text-gray-700">Updating graphic...</span>
          </div>
        </div>
      )}
    </div>
  )
}
